import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAdsAccess } from '@/api/ads/hooks';
import { useAuth } from '@/auth/AuthProvider';
import type { AccessResponse } from '@/api/ads/types';

interface AccessState {
  ready: boolean;
  isSuperuser: boolean;
  can: (slug: string, permission?: string) => boolean;
  data: AccessResponse | undefined;
}

const AccessContext = createContext<AccessState | null>(null);

export function AccessProvider({ children }: { children: ReactNode }) {
  const { isAdmin } = useAuth();
  const { data, isLoading } = useAdsAccess(isAdmin);

  const value = useMemo<AccessState>(() => {
    const map = new Map<string, Record<string, boolean>>();
    (data?.resources ?? []).forEach((r) => map.set(r.slug, r.permissions));
    const isSuperuser = !!data?.is_superuser;
    return {
      ready: !isLoading,
      isSuperuser,
      data,
      can: (slug: string, permission = 'view') => {
        if (isSuperuser) return true;
        const perms = map.get(slug);
        return !!perms && !!perms[permission];
      },
    };
  }, [data, isLoading]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): AccessState {
  const ctx = useContext(AccessContext);
  if (!ctx) throw new Error('useAccess must be used within AccessProvider');
  return ctx;
}
